import { RobotAvatar, robotColor } from "@/entities/robot";
import type { components } from "@/api/types";
import { formatMoney, formatPercent } from "@/shared/lib";

type RobotIncome = components["schemas"]["RobotIncome"];

/**
 * Строка «доход по роботам»: аватар, точка цвета, имя, сумма, полоска доли.
 *
 * Цвет задаётся ОДИН раз на строке, а точка, сумма и заливка полоски читают
 * `currentColor`. Так строка красится акцентом робота целиком, а не тремя
 * согласованными правилами, которые однажды разъедутся.
 *
 * Ширина полоски приходит с бэкенда целым процентом (`progress_pct`) — считать
 * долю на клиенте запрещено правилом ТЗ №5, и это не формальность: доля должна
 * сходиться в 100% по всем строкам, а округление на клиенте её разбалансирует.
 */
export function RobotIncomeRow({ item }: { item: RobotIncome }) {
  return (
    <article
      className="grid min-h-11 grid-cols-[29px_9px_1fr_auto] items-center gap-1.5 border-b border-border-hair py-2 last:border-b-0"
      style={{ color: robotColor(item.robot) }}
    >
      <RobotAvatar robot={item.robot} size={28} />
      <span aria-hidden="true" className="size-2 rounded-full bg-current shadow-[0_0_var(--glow-blur-sm)_currentColor]" />

      <span className="flex min-w-0 flex-col gap-1">
        <span className="flex items-center justify-between gap-2 text-small">
          <span className="truncate font-semibold text-text-primary">{item.robot.name}</span>
          <span className="shrink-0 font-semibold">
            {formatMoney(item.earned, { sign: "always", suffix: "USDT" })}
          </span>
        </span>

        <span className="h-0.5 w-full rounded-full bg-white/8">
          <span
            className="block h-full rounded-full bg-current shadow-[0_0_4px_currentColor]"
            style={{ width: `${String(item.progress_pct)}%` }}
          />
        </span>
      </span>

      <span className="w-8 shrink-0 text-right text-caption text-text-secondary">
        {formatPercent(item.share_pct, { precision: 0 })}
      </span>
    </article>
  );
}
